<template>
  <div class="pm-root">

    <!-- Header -->
    <div class="pm-header">
      <div>
        <h2 class="pm-title">Permissões de Acesso</h2>
        <p class="pm-sub">Gerencie quais módulos cada usuário pode visualizar no menu</p>
      </div>
      <button class="pm-btn-refresh" @click="loadData" :disabled="loading">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/>
        </svg>
        Atualizar
      </button>
    </div>

    <!-- Legend -->
    <div class="pm-legend">
      <div class="pm-legend-item" v-for="mod in modulesCatalog" :key="mod.key">
        <span class="pm-legend-dot" :style="{ background: mod.color }"></span>
        <span>{{ mod.label }}</span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="pm-loading">
      <div class="pm-spinner"></div>
      <span>Carregando usuários e permissões...</span>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="pm-error">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      {{ error }}
    </div>

    <!-- Filtro -->
    <div v-else class="pm-toolbar">
      <div class="pm-search-wrap">
        <svg class="pm-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input v-model="search" class="pm-search" placeholder="Buscar por nome ou e-mail..." />
      </div>
      <select v-model="roleFilter" class="pm-select">
        <option value="">Todos os perfis</option>
        <option value="admin">Admin</option>
        <option value="client">Cliente</option>
        <option value="aluno">Aluno</option>
      </select>
    </div>

    <!-- Tabela -->
    <div class="pm-table-wrap" v-if="!loading && !error">
      <table class="pm-table">
        <thead>
          <tr>
            <th class="pm-th pm-th-user">Usuário</th>
            <th class="pm-th pm-th-role">Perfil</th>
            <th
              v-for="mod in modulesCatalog"
              :key="mod.key"
              class="pm-th pm-th-module"
            >
              <div class="pm-th-mod-inner">
                <span class="pm-th-dot" :style="{ background: mod.color }"></span>
                {{ mod.label }}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredUsers.length === 0">
            <td :colspan="3 + modulesCatalog.length" class="pm-empty">Nenhum usuário encontrado.</td>
          </tr>
          <tr
            v-for="user in filteredUsers"
            :key="user.id"
            class="pm-row"
          >
            <!-- Usuário -->
            <td class="pm-td pm-td-user">
              <div class="pm-user-info">
                <div class="pm-avatar" :style="{ background: avatarColor(user.user_name) }">
                  {{ initials(user.user_name) }}
                </div>
                <div>
                  <div class="pm-user-name">{{ user.user_name || '—' }}</div>
                  <div class="pm-user-email">{{ user.user_email }}</div>
                </div>
              </div>
            </td>
            <!-- Perfil -->
            <td class="pm-td">
              <span class="pm-role-badge" :class="'pm-role-' + (user.user_role || 'client')">
                {{ user.user_role || 'client' }}
              </span>
            </td>
            <!-- Toggles por módulo -->
            <td
              v-for="mod in modulesCatalog"
              :key="mod.key"
              class="pm-td pm-td-toggle"
            >
              <div class="pm-toggle-wrap">
                <button
                  class="pm-toggle"
                  :class="{ 'pm-toggle-on': hasAbility(user, mod.abilityIds), 'pm-toggle-saving': isSaving(user.id, mod.key) }"
                  @click="toggleAbility(user, mod)"
                  :disabled="isSaving(user.id, mod.key)"
                  :title="hasAbility(user, mod.abilityIds) ? 'Clique para revogar acesso' : 'Clique para liberar acesso'"
                >
                  <span class="pm-toggle-thumb"></span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Toast -->
    <transition name="pm-toast-fade">
      <div v-if="toast.show" class="pm-toast" :class="'pm-toast-' + toast.type">
        <svg v-if="toast.type === 'success'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        {{ toast.message }}
      </div>
    </transition>

  </div>
</template>

<script>
import { useApi } from '@/composables/useApi'

const api = useApi()

export default {
  name: 'PermissoesAdmin',
  data() {
    return {
      loading: false,
      error: null,
      users: [],
      catalog: [],
      search: '',
      roleFilter: '',
      savingMap: {},
      toast: { show: false, type: 'success', message: '' },
      toastTimer: null,

      // Módulos do menu — mapeados para ability_ids do banco (tb_ability)
      // 1=manage/all (admin), 2=read/ACL, 3=read/Auth, 4=vslbooster, 5=chatbooster,
      // 12=manage/TOUR_VIRTUAL, 13=read/CURSOS
      modulesCatalog: [
        {
          key: 'admin',
          label: 'Administração',
          color: '#E86A10',
          abilityIds: [1],
          description: 'Acesso ao painel admin, usuários, permissões, chat suporte',
        },
        {
          key: 'vslbooster',
          label: 'VSL Booster',
          color: '#3b82f6',
          abilityIds: [4],
          description: 'Meus Vídeos, Webinário, Analytics, Universidade Corp',
        },
        {
          key: 'chatbooster',
          label: 'Chat Booster',
          color: '#10b981',
          abilityIds: [5],
          description: 'Chat, Fluxos, Leads, Analytics de chat',
        },
        {
          key: 'cursos',
          label: 'Cursos',
          color: '#8b5cf6',
          abilityIds: [13],
          description: 'Meus Cursos, Módulos de Treinamento',
        },
        {
          key: 'basico',
          label: 'Acesso Básico',
          color: '#64748b',
          abilityIds: [2, 3],
          description: 'ACL + Auth — necessário para login e navegação básica',
        },
      ],
    }
  },
  computed: {
    filteredUsers() {
      let result = this.users
      if (this.search.trim()) {
        const q = this.search.trim().toLowerCase()
        result = result.filter(u =>
          (u.user_name || '').toLowerCase().includes(q) ||
          (u.user_email || '').toLowerCase().includes(q)
        )
      }
      if (this.roleFilter) {
        result = result.filter(u => (u.user_role || 'client') === this.roleFilter)
      }
      return result
    },
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      this.error = null
      try {
        const [usersRes, catalogRes] = await Promise.all([
          api.get('/usersWithAbilities'),
          api.get('/abilityCatalog'),
        ])
        this.users = usersRes.data || []
        this.catalog = catalogRes.data || []
      } catch (err) {
        this.error = 'Erro ao carregar dados. Verifique a conexão com o servidor.'
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    hasAbility(user, abilityIds) {
      if (!user.abilities || !Array.isArray(user.abilities)) return false
      return abilityIds.some(id =>
        user.abilities.some(a => a.ability_id === id)
      )
    },

    isSaving(userId, moduleKey) {
      return !!this.savingMap[`${userId}-${moduleKey}`]
    },

    async toggleAbility(user, mod) {
      const key = `${user.id}-${mod.key}`
      if (this.savingMap[key]) return

      const currentlyHas = this.hasAbility(user, mod.abilityIds)

      this.$set(this.savingMap, key, true)

      try {
        if (currentlyHas) {
          // Revogar: remove todos os ability_ids do módulo para o usuário
          for (const abilityId of mod.abilityIds) {
            await api.delete(`/abilityUser/${user.id}/${abilityId}`)
          }
          // Atualiza localmente
          user.abilities = user.abilities.filter(a => !mod.abilityIds.includes(a.ability_id))
          this.showToast('success', `Acesso "${mod.label}" revogado de ${user.user_name || user.user_email}`)
        } else {
          // Conceder: adiciona todos os ability_ids do módulo para o usuário
          for (const abilityId of mod.abilityIds) {
            const res = await api.post('/abilityUser', { user_id: user.id, ability_id: abilityId })
            if (res.data && res.data.insertId) {
              user.abilities.push({ ability_id: abilityId, assignment_id: res.data.insertId })
            } else {
              // INSERT IGNORE pode não retornar insertId — busca assignments atuais
              user.abilities.push({ ability_id: abilityId, assignment_id: null })
            }
          }
          this.showToast('success', `Acesso "${mod.label}" concedido a ${user.user_name || user.user_email}`)
        }
      } catch (err) {
        this.showToast('error', `Erro ao atualizar permissão. Tente novamente.`)
        console.error(err)
      } finally {
        this.$delete(this.savingMap, key)
      }
    },

    showToast(type, message) {
      clearTimeout(this.toastTimer)
      this.toast = { show: true, type, message }
      this.toastTimer = setTimeout(() => { this.toast.show = false }, 3500)
    },

    initials(name) {
      if (!name) return '?'
      return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
    },

    avatarColor(name) {
      if (!name) return '#64748b'
      const colors = ['#E86A10', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4']
      let hash = 0
      for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
      return colors[Math.abs(hash) % colors.length]
    },
  },
}
</script>

<style scoped>
/* ─── Root ──────────────────────────────────────────────────────────────── */
.pm-root {
  padding: 24px;
  min-height: 100%;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ─── Header ────────────────────────────────────────────────────────────── */
.pm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.pm-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--dk-text-primary, #e2e8f0);
  margin: 0 0 4px;
}
.pm-sub {
  font-size: 0.82rem;
  color: var(--dk-text-muted, rgba(255,255,255,0.4));
  margin: 0;
}
.pm-btn-refresh {
  display: flex;
  align-items: center;
  gap: 7px;
  background: var(--dk-bg-card, rgba(255,255,255,0.04));
  border: 1.5px solid var(--dk-border, rgba(255,255,255,0.1));
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--dk-text-primary, #e2e8f0);
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  white-space: nowrap;
}
.pm-btn-refresh:hover { border-color: #E86A10; color: #E86A10; }
.pm-btn-refresh:disabled { opacity: 0.5; cursor: not-allowed; }

/* ─── Legend ────────────────────────────────────────────────────────────── */
.pm-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-bottom: 20px;
}
.pm-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: var(--dk-text-secondary, rgba(255,255,255,0.55));
  font-weight: 500;
}
.pm-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* ─── Loading / Error ───────────────────────────────────────────────────── */
.pm-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: var(--dk-text-muted, rgba(255,255,255,0.4));
  font-size: 0.9rem;
}
.pm-spinner {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid var(--dk-border, rgba(255,255,255,0.1));
  border-top-color: #E86A10;
  animation: pm-spin 0.7s linear infinite;
  flex-shrink: 0;
}
@keyframes pm-spin { to { transform: rotate(360deg); } }

.pm-error {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(248,113,113,0.12);
  border: 1px solid rgba(248,113,113,0.25);
  border-radius: 10px;
  padding: 16px 20px;
  color: #f87171;
  font-size: 0.88rem;
  font-weight: 500;
  margin-bottom: 20px;
}

/* ─── Toolbar ───────────────────────────────────────────────────────────── */
.pm-toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.pm-search-wrap {
  position: relative;
  flex: 1;
  min-width: 200px;
}
.pm-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--dk-text-faint, rgba(255,255,255,0.3));
  pointer-events: none;
}
.pm-search {
  width: 100%;
  padding: 9px 12px 9px 36px;
  border: 1.5px solid var(--dk-border, rgba(255,255,255,0.1));
  border-radius: 8px;
  font-size: 0.85rem;
  color: var(--dk-text-primary, #e2e8f0);
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.pm-search:focus { border-color: #E86A10; }

.pm-select {
  padding: 9px 14px;
  border: 1.5px solid var(--dk-border, rgba(255,255,255,0.1));
  border-radius: 8px;
  font-size: 0.85rem;
  color: var(--dk-text-primary, #e2e8f0);
  outline: none;
  cursor: pointer;
  background: var(--dk-bg-card, rgba(255,255,255,0.04));
  transition: border-color 0.15s;
}
.pm-select:focus { border-color: #E86A10; }

/* ─── Table ─────────────────────────────────────────────────────────────── */
.pm-table-wrap {
  background: var(--dk-bg-card, rgba(255,255,255,0.04));
  border-radius: 14px;
  border: 1.5px solid var(--dk-border-light, rgba(255,255,255,0.07));
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  overflow-x: auto;
}
.pm-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 680px;
}
.pm-th {
  padding: 14px 16px;
  text-align: left;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--dk-text-muted, rgba(255,255,255,0.4));
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1.5px solid var(--dk-border-light, rgba(255,255,255,0.07));
  white-space: nowrap;
  background: var(--dk-bg-card-hover, rgba(255,255,255,0.05));
}
.pm-th:first-child { border-radius: 12px 0 0 0; }
.pm-th:last-child { border-radius: 0 12px 0 0; }
.pm-th-module { text-align: center; }
.pm-th-mod-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}
.pm-th-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.pm-row {
  transition: background 0.12s;
}
.pm-row:hover { background: var(--dk-bg-card-hover, rgba(255,255,255,0.05)); }
.pm-row + .pm-row { border-top: 1px solid var(--dk-border-light, rgba(255,255,255,0.05)); }

.pm-td {
  padding: 14px 16px;
  vertical-align: middle;
}
.pm-td-user { min-width: 220px; }
.pm-td-toggle { text-align: center; }

/* ─── User info ─────────────────────────────────────────────────────────── */
.pm-user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}
.pm-avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}
.pm-user-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--dk-text-primary, #e2e8f0);
  line-height: 1.2;
}
.pm-user-email {
  font-size: 0.74rem;
  color: var(--dk-text-faint, rgba(255,255,255,0.3));
  margin-top: 1px;
}

/* ─── Role badge ─────────────────────────────────────────────────────────── */
.pm-role-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.pm-role-admin { background: rgba(232,106,16,0.1); color: #E86A10; }
.pm-role-client { background: rgba(59,130,246,0.1); color: #3b82f6; }
.pm-role-aluno { background: rgba(139,92,246,0.1); color: #8b5cf6; }

/* ─── Toggle ─────────────────────────────────────────────────────────────── */
.pm-toggle-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}
.pm-toggle {
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 11px;
  border: none;
  cursor: pointer;
  background: rgba(255,255,255,0.12);
  transition: background 0.22s ease;
  outline: none;
  padding: 0;
  flex-shrink: 0;
}
.pm-toggle.pm-toggle-on { background: #28c76f; }
.pm-toggle.pm-toggle-saving {
  opacity: 0.6;
  cursor: wait;
  animation: pm-pulse 0.8s ease infinite;
}
@keyframes pm-pulse { 0%,100% { opacity: 0.6; } 50% { opacity: 0.3; } }

.pm-toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--dk-bg-card, rgba(255,255,255,0.04));
  box-shadow: 0 1px 3px rgba(0,0,0,0.18);
  transition: transform 0.22s ease;
  display: block;
}
.pm-toggle.pm-toggle-on .pm-toggle-thumb { transform: translateX(18px); }

/* ─── Empty ─────────────────────────────────────────────────────────────── */
.pm-empty {
  text-align: center;
  padding: 40px;
  color: var(--dk-text-faint, rgba(255,255,255,0.3));
  font-size: 0.88rem;
}

/* ─── Toast ─────────────────────────────────────────────────────────────── */
.pm-toast {
  position: fixed;
  bottom: 28px;
  right: 28px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  z-index: 9999;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  max-width: 340px;
}
.pm-toast-success { background: #28c76f; color: #fff; }
.pm-toast-error   { background: #C12B2B; color: #fff; }

.pm-toast-fade-enter-active, .pm-toast-fade-leave-active { transition: all 0.25s ease; }
.pm-toast-fade-enter, .pm-toast-fade-leave-to { opacity: 0; transform: translateY(12px); }
</style>
